import React from 'react';
import { usePageMeta } from '../hooks/usePageMeta';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useNavigate } from 'react-router-dom';
import { getTranslation } from "../components/translations";

const LOGO_URL = "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/69891104ffce19fb70f11876/283cb7c40_blackbg.png";

export default function Terms() {
  const t = (key) => getTranslation(key);
  const navigate = useNavigate();

  usePageMeta({
    title: 'Terms and Conditions | Active Gaming',
    description: 'Review the terms and conditions for Active Gaming products and services, including VSteps and V4LLUED Guns Joy-Con controllers. Your rights and obligations explained.',
    ogImage: `${window.location.origin}/v4logo.png`,
    ogUrl: window.location.origin + '/Terms',
  });

  return (
    <div className="min-h-screen bg-[#0B0C10] text-white">
      {/* Ambient Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-gray-800/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-gray-700/8 rounded-full blur-[120px]" />
      </div>

      {/* Header */}
      <header className="relative z-10 bg-[#0B0C10]/80 backdrop-blur-xl border-b border-white/5">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="VSteps" className="h-8 sm:h-10 w-auto" />
          </div>
          
          <Button 
            onClick={() => navigate('/steps')}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/5 bg-transparent"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('backToHome')}
          </Button>
        </nav>
      </header>

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 rounded-2xl bg-[#FF0000]/10 flex items-center justify-center">
              <FileText className="w-8 h-8 text-[#FF0000]" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold">{t('termsConditions')}</h1>
              <p className="text-[#C5C6C7] mt-2">{t('lastUpdatedTerms')}</p>
            </div>
          </div>

          <div className="bg-[#1a1a1a]/60 rounded-3xl p-6 sm:p-8 md:p-12 border border-white/5 space-y-8">
            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('agreementToTerms')}</h2>
              <p className="text-[#C5C6C7] leading-relaxed">
                {t('agreementDesc')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('licenseUsage')}</h2>
              <div className="space-y-4">
                <p className="text-[#C5C6C7] leading-relaxed">
                  {t('licenseDesc')}
                </p>
                <p className="text-[#C5C6C7] leading-relaxed">
                  {t('youMayNot')}
                </p>
                <ul className="list-disc list-inside text-[#C5C6C7] space-y-2 ml-4">
                  <li>{t('licenseItem1')}</li>
                  <li>{t('licenseItem2')}</li>
                  <li>{t('licenseItem3')}</li>
                  <li>{t('licenseItem4')}</li>
                  <li>{t('licenseItem5')}</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('healthSafety')}</h2>
              <div className="space-y-4">
                <p className="text-[#C5C6C7] leading-relaxed">
                  {t('healthDesc')}
                </p>
                <ul className="list-disc list-inside text-[#C5C6C7] space-y-2 ml-4">
                  <li>{t('healthItem1')}</li>
                  <li>{t('healthItem2')}</li>
                  <li>{t('healthItem3')}</li>
                  <li>{t('healthItem4')}</li>
                  <li>{t('healthItem5')}</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('gameCompatibility')}</h2>
              <p className="text-[#C5C6C7] leading-relaxed">
                {t('gameCompatibilityDesc')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('networkRequirements')}</h2>
              <p className="text-[#C5C6C7] leading-relaxed">
                {t('networkRequirementsDesc')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('subscriptionPayments')}</h2>
              <div className="space-y-4">
                <p className="text-[#C5C6C7] leading-relaxed">
                  {t('subscriptionDesc')}
                </p>
                <ul className="list-disc list-inside text-[#C5C6C7] space-y-2 ml-4">
                  <li>{t('subItem1')}</li>
                  <li>{t('subItem2')}</li>
                  <li>{t('subItem3')}</li>
                  <li>{t('subItem4')}</li>
                  <li>{t('subItem5')}</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('competitiveFeatures')}</h2>
              <p className="text-[#C5C6C7] leading-relaxed">
                {t('competitiveFeaturesIntro')}
              </p>
              <ul className="list-disc list-inside text-[#C5C6C7] space-y-2 ml-4">
                <li>{t('compItem1')}</li>
                <li>{t('compItem2')}</li>
                <li>{t('compItem3')}</li>
                <li>{t('compItem4')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('intellectualProperty')}</h2>
              <p className="text-[#C5C6C7] leading-relaxed">
                {t('intellectualPropertyDesc')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('limitationLiability')}</h2>
              <p className="text-[#C5C6C7] leading-relaxed">
                {t('limitationLiabilityDesc')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('userConduct')}</h2>
              <p className="text-[#C5C6C7] leading-relaxed">
                {t('userConductIntro')}
              </p>
              <ul className="list-disc list-inside text-[#C5C6C7] space-y-2 ml-4">
                <li>{t('conductItem1')}</li>
                <li>{t('conductItem2')}</li>
                <li>{t('conductItem3')}</li>
                <li>{t('conductItem4')}</li>
                <li>{t('conductItem5')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('termination')}</h2>
              <p className="text-[#C5C6C7] leading-relaxed">
                {t('terminationDesc')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('changesToTerms')}</h2>
              <p className="text-[#C5C6C7] leading-relaxed">
                {t('changesToTermsDesc')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('governingLaw')}</h2>
              <p className="text-[#C5C6C7] leading-relaxed">
                {t('governingLawDesc')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('contactInfo')}</h2>
              <p className="text-[#C5C6C7] leading-relaxed">
                {t('contactInfoDesc')}
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
}